import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { Page, ProcessHeader } from '@/components';
import RadioButton from '@/components/RadioButton';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { Button, Text } from '@mantine/core';
import { getMutationResult } from '@/helpers/query';
import { useGetAllSupportTypeMutation, useSaveMutation, useSendingToTheCustomerForSignatureMutation } from '@/services/api';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { updateProposalOffer } from '@/data/features/calculate/calculate';
import { useNavigate } from 'react-router-dom';
import {
  IconSend,
} from '@tabler/icons-react';

import { notifications } from '@mantine/notifications';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import FirstBorrowerIcon from '@/assets/images/firstBorrower.svg?react'; // eslint-disable-line import/no-unresolved
import TwoPeopleShakingHandsIcon from '@/assets/images/twoPeopleShakingHands.svg?react'; // eslint-disable-line import/no-unresolved
import UsersHandShakeIcon from '@/assets/images/usersHandShake.svg?react'; // eslint-disable-line import/no-unresolved
import Modal from '../../../components/Modal';
import classes from './routeType.module.css';

const RouteType = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [apiGetAllSupportTypes] = useGetAllSupportTypeMutation();
  const [apiSave] = useSaveMutation();
  const [apiConfigSendToClientForSignature] = useSendingToTheCustomerForSignatureMutation();
  const [routeOptionsArray, setRouteOptionsArray] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [routeTypeError, setRouteTypeError] = useState('');
  const proposalOffer = useSelector((state) => state.proposalOffer);
  const borrowersStore = useSelector((state) => state.proposal.borrower);
  const proposal = useSelector((state) => state.proposal);
  const { id } = proposal;
  const { loanTypeName } = proposal.extraDetails;
  useIdInStoreGuard();

  // Get the first borrower (main one)
  const firstBorrower = borrowersStore[0];

  const handleSendLink = async () => {
    try {
      setIsSubmitting(true);
      const { values } = form;

      dispatch(updateProposalOffer({
        clientRouteType: values.clientRouteType,
        routeType: values.routeType,
      }));

      // First, save the route type
      const objectToSend = {
        id,
        routeType: values.routeType,
      };

      await getMutationResult(objectToSend, apiSave);

      // Then send the client signature request
      const clientSignatureObject = {
        proposalNumber: id,
        email: firstBorrower?.email,
        // phone: firstBorrower?.phoneNumber,
        fullName: firstBorrower?.fullName,
      };

      const { data, isError } = await getMutationResult(
        clientSignatureObject,
        apiConfigSendToClientForSignature,
      );

      if (!isError || data.success) {
        setShowModal(false);
        navigate(routeWithParams(getRouteById(routes.newProposal.id, 'success').path, { id }));
      } else {
        throw new Error('Failed to send client signature request');
      }
    } catch (error) {
      console.error('Error in submission:', error);
      notifications.show({
        title: 'שגיאה',
        message: 'אירעה שגיאה בשליחת הנתונים',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      routeType: proposalOffer.routeType || '',
      clientRouteType: proposalOffer.clientRouteType || '',
      selectedClient: proposalOffer.selectedClient || '',
    },
    validate: {
      routeType: (value) => (value ? null : t('globals.validations.required')),
      clientRouteType: (value) => (value ? null : 'Required'),
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    const fetchStatusTypes = async () => {
      try {
        const { data: result } = await getMutationResult({}, apiGetAllSupportTypes);
        const { success, data } = result;

        if (success) {
          const filteredData = data.filter((op) => op.name !== 'selfSupport');
          const transformedOptions = filteredData.map((option) => ({
            value: option._id,
            icon: icons[option.icon],
            label: option.hebrew,
            description: option.description,
          }));
          setRouteOptionsArray(transformedOptions);
        } else {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
        }
      } catch (error) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      }
    };

    fetchStatusTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReturn = () => {
    if (loanTypeName === 'mortgageRefinancing') {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'mortgageCycleSummary').path, { id }));
    } else {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'summary').path, { id }));
    }
  };

  const icons = {
    iconUser: <FirstBorrowerIcon />,
    iconUsers: <TwoPeopleShakingHandsIcon />,
  };

  const handleSubmit = () => {
    if (!form.values.routeType) {
      setRouteTypeError(t('pages.routeType.errors.routeType'));
      return;
    }
    setRouteTypeError('');
    setShowModal(true);
  };

  return (
    <ProcessHeader
      headerText={t('pages.routeType.title')}
      headerColor="#E0EDFF"
      headerTextColor="#3348FF"
      subtitle={t('pages.routeType.subTitle')}
      showReturnButton
      onReturn={handleReturn}
      icon={UsersHandShakeIcon}
    >

      <form>
        <Page.Section>
          <RadioButton
            options={routeOptionsArray}
            fieldName="routeType"
            form={form}
            value={form.values.routeType}
            onChange={(value) => {
              form.setFieldValue('routeType', value);
              setRouteTypeError('');
            }}
          />
          {routeTypeError && (
            <Text color='red' pt={'lg'}>{routeTypeError}</Text>
          )}
        </Page.Section>

        <Page.Actions>
          <Button
            variant="filled"
            onClick={handleSubmit}
            rightSection={<IconSend size={16} />}
            className={classes.submitButton}
          >
            שליחה ללקוחות עם חתימה ותשלום
          </Button>
        </Page.Actions>
      </form>
      {showModal && (
        <Modal
          opened={showModal}
          onClose={() => setShowModal(false)}
          centered
          size="lg"
          showClose={false}
          title={
            <div className={classes.modalTitle}>
              <IconSend size={20} className={classes.modalTitleIcon} />
              <h2 className={classes.modalTitleText}>
                אישור לפני שליחה ללקוח
              </h2>
            </div>
          }
          styles={{
            title: classes.modalTitleStyles,
          }}
        >
          <div className={classes.modalContent}>
            <div className={classes.infoBox}>
              <h3 className={classes.infoBoxTitle}>
                קישור עם מידע להשלמת התהליך ולתשלום עבור מסלול הייעוץ הנבחר יישלח
                בהודעת SMS ובדוא״ל ללקוח (לווה ראשי)
              </h3>
              <div className={classes.borrowerInfoBox}>
                <strong className={classes.borrowerName}>{firstBorrower?.fullName}</strong>
                <br />
                <span className={classes.borrowerEmail}>{firstBorrower?.email}</span>
              </div>
              <p className={classes.footerText}>
                ניתן לעקוב אחר סטטוס התהליך במערכת.
              </p>

            </div>

            <div className={classes.warningBox}>
              <h4 className={classes.warningTitle}>
                ⚠️ לתשומת לבך
              </h4>
              <p className={classes.warningText}>
                לאחר שליחת הקישור לא ניתן יהיה לבצע שינויים בנתונים שהוקלדו.
                <br />
                נא לוודא שכל המידע נכון לפני השליחה.
              </p>
            </div>

            <div className={classes.buttonContainer}>
              <Button
                variant="filled"
                onClick={() => setShowModal(false)}
                className={classes.continueButtonRoot}
              >
                ביטול
              </Button>

              <Button
                variant="filled"
                onClick={handleSendLink}
                loading={isSubmitting}
                disabled={isSubmitting}
                className={classes.submitButton}
              >
                {isSubmitting ? 'שולח...' : 'שלח קישור'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </ProcessHeader>
  );
};

export default RouteType;
